import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supportApi } from '@/api/support';
import { DirectInbox, Lifebuoy, Add, Clock } from '@/components/icons';
import { EmptyState, ListSection, PageHeader, PageShell } from '@/components/system';
import { Button } from '@/components/ui/button';
import Loading from '@/components/ui/Loading';
import { useModalContext } from '@/components/ui/modal-provider';
import { SupportTicketPage } from '@/pages/constants';
import { createPageUrl } from '@/utils';
import { SupportTicketCard } from './components/SupportTicketCard';
import { SupportTicketForm } from './components/SupportTicketForm';

const getTicketUrl = (ticketId: string) =>
    createPageUrl(SupportTicketPage).replace(':ticketid', ticketId);

export default function SupportPage() {
    const { openModal } = useModalContext();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const { data: tickets = [], isLoading } = useQuery({
        queryKey: ['support', 'tickets', 'all'],
        queryFn: () => supportApi.listTickets('all'),
    });

    const openCreateTicketModal = () => {
        openModal({
            title: 'Abrir chamado de suporte',
            description: 'Conte o que aconteceu para que possamos investigar dentro da plataforma.',
            size: 'lg',
            // The form brings its own layout so its actions land in a sticky
            // footer; the shell body must not add a second scroll or padding.
            bodyClassName: 'flex min-h-0 flex-col overflow-hidden p-0',
            content: (
                <SupportTicketForm
                    onCreated={(ticket) => {
                        queryClient.invalidateQueries({
                            queryKey: ['support', 'tickets'],
                        });
                        navigate(getTicketUrl(ticket.id));
                    }}
                />
            ),
        });
    };

    return (
        <PageShell width="wide">
            <PageHeader
                title="Suporte"
                icon={Lifebuoy}
                meta={
                    isLoading || tickets.length === 0 ? undefined : (
                        <span aria-live="polite">
                            {tickets.length} chamado{tickets.length === 1 ? '' : 's'}
                        </span>
                    )
                }
                actions={
                    <Button onClick={openCreateTicketModal}>
                        <Add className="size-4" />
                        Novo chamado
                    </Button>
                }
            />

            <ListSection
                title="Meus chamados"
                description="Acompanhe as conversas abertas com o suporte."
                controls={
                    <p className="flex items-center gap-2 text-sm text-muted-foreground lg:pt-1">
                        <Clock className="size-4 shrink-0" />
                        Atendimento de segunda a sexta, das 9h às 18h.
                    </p>
                }
                contentClassName="p-6"
            >
                {isLoading ? (
                    <Loading className="h-auto py-8" size={160} />
                ) : tickets.length === 0 ? (
                    <EmptyState
                        icon={DirectInbox}
                        title="Nenhum chamado aberto"
                        description="Quando precisar de ajuda, abra um chamado com uma descrição objetiva do problema."
                        action={
                            <Button onClick={openCreateTicketModal}>
                                <Add className="size-4" />
                                Abrir primeiro chamado
                            </Button>
                        }
                    />
                ) : (
                    <div className="grid gap-4 lg:grid-cols-2">
                        {tickets.map((ticket) => (
                            <SupportTicketCard
                                key={ticket.id}
                                ticket={ticket}
                                to={getTicketUrl(ticket.id)}
                            />
                        ))}
                    </div>
                )}
            </ListSection>
        </PageShell>
    );
}
